import * as React from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'

import { cn } from '@strata-monorepo/shared-ui/utils/cn'

/**
 * Default thumb halo, shared so callers overriding `thumbClassName` (e.g. to show the
 * halo only while dragging) can restore the exact same shadow instead of re-typing it.
 */
export const SLIDER_THUMB_HALO_SHADOW = 'strata:shadow-[0px_0px_0px_4px_#8097B033]'

interface SliderProps extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  trackClassName?: string
  rangeClassName?: string
  thumbClassName?: string
  showEndTicks?: boolean
  thumbProps?: Omit<React.ComponentPropsWithoutRef<typeof SliderPrimitive.Thumb>, 'className'>
}

const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    {
      className,
      trackClassName,
      rangeClassName,
      thumbClassName,
      showEndTicks = true,
      thumbProps,
      ...props
    },
    ref,
  ) => (
    <div className='strata:relative'>
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          'strata:relative strata:flex strata:w-full strata:touch-none strata:select-none strata:items-center',
          showEndTicks &&
            "strata:before:content-[''] strata:before:absolute strata:before:bg-gray-500 strata:before:h-4 strata:before:left-0 strata:before:w-0.5",
          showEndTicks &&
            "strata:after:content-[''] strata:after:absolute strata:after:bg-gray-500 strata:after:h-4 strata:after:right-0 strata:after:w-0.5",
          className,
        )}
        {...props}
      >
        <SliderPrimitive.Track
          className={cn(
            'strata:relative strata:h-0.5 strata:w-full strata:grow strata:overflow-hidden strata:rounded-full strata:bg-gray-500',
            trackClassName,
          )}
        >
          <SliderPrimitive.Range
            className={cn('strata:absolute strata:h-full strata:bg-gray-500', rangeClassName)}
          />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          {...thumbProps}
          className={cn(
            'strata:block strata:h-4 strata:w-4 strata:rounded-full strata:bg-[#415973]',
            SLIDER_THUMB_HALO_SHADOW,
            'strata:transition-colors strata:outline-none strata:focus-visible:focus-strata strata:disabled:pointer-events-none strata:disabled:opacity-50',
            thumbClassName,
          )}
        />
      </SliderPrimitive.Root>
    </div>
  ),
)
Slider.displayName = SliderPrimitive.Root.displayName

export { Slider }
